import { Box, Button, Card, Heading, HStack, SimpleGrid, Text, VStack } from '@chakra-ui/react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  ArrowRight,
  Cpu,
  Database,
  Globe,
  Lock,
  MessageCircle,
  Network,
  ServerCog,
} from 'lucide-react'
import { cardHover, PageHeader, SectionHeading } from '@/components/shell'

export const Route = createFileRoute('/_app/education/')({
  component: EducationHubPage,
})

interface TopicCardProps {
  icon: typeof MessageCircle
  title: string
  tagline: string
  description: string
  to: string
}

function TopicCard({ icon: Icon, title, tagline, description, to }: TopicCardProps) {
  const navigate = useNavigate()
  return (
    <Card.Root
      h="100%"
      transition="all 0.3s"
      _hover={cardHover}
      cursor="pointer"
      onClick={() => navigate({ to })}
    >
      <Card.Body p={6}>
        <VStack align="start" gap={4} h="100%">
          <HStack gap={3}>
            <Box p={3} bg="brand.solid" borderRadius="xl">
              <Icon size={24} />
            </Box>
            <Text
              fontSize="xs"
              fontWeight="bold"
              color="content.secondary"
              textTransform="uppercase"
              letterSpacing="wide"
            >
              {tagline}
            </Text>
          </HStack>
          <VStack align="start" gap={2}>
            <Heading size="md">{title}</Heading>
            <Text fontSize="sm" color="content.secondary">
              {description}
            </Text>
          </VStack>
          <Button
            variant="ghost"
            size="sm"
            color="brand.solid"
            mt="auto"
            alignSelf="flex-start"
            px={0}
            onClick={(e) => {
              e.stopPropagation()
              navigate({ to })
            }}
          >
            Explore
            <ArrowRight size={14} />
          </Button>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

const tags = ['Foundations', 'Standards', 'Architecture', 'Operations', 'Security']

function EducationHubPage() {
  const topics: TopicCardProps[] = [
    {
      icon: MessageCircle,
      title: 'MQTT',
      tagline: 'Protocol',
      description:
        'The publish/subscribe protocol that powers modern IoT and industrial data streaming. Learn the core primitives, QoS levels, and why MQTT is the messaging backbone for HiveMQ.',
      to: '/education/mqtt',
    },
    {
      icon: Cpu,
      title: 'Sparkplug',
      tagline: 'OT Standard',
      description:
        'A specification on top of MQTT that brings stateful, OT-grade context to your namespace: birth and death certificates, alias compression, and structured metric definitions.',
      to: '/education/sparkplug',
    },
    {
      icon: Network,
      title: 'Unified Namespace',
      tagline: 'Architecture',
      description:
        'One hierarchical topic tree that becomes the single source of truth across plant, line, and enterprise. The architectural pattern behind every successful Industry 4.0 rollout.',
      to: '/education/uns',
    },
    {
      icon: ServerCog,
      title: 'HiveMQ Cluster',
      tagline: 'Operations',
      description:
        'How a HiveMQ broker cluster handles zero-downtime rolling upgrades, replication factor 2, and 30K+ clients without dropping a message. Interactive 11-step simulation.',
      to: '/education/hivemq-cluster',
    },
    {
      icon: Lock,
      title: 'TLS & mTLS',
      tagline: 'Security',
      description:
        'How the broker and clients authenticate each other with keystores and truststores. Step through a one-way TLS and a mutual-TLS handshake, including a rejected untrusted client.',
      to: '/education/tls-mtls',
    },
    {
      icon: Database,
      title: 'DataHub',
      tagline: 'Governance',
      description:
        'Schema enforcement, validation, and data policies at the broker. Reject bad payloads before they reach downstream systems, no application changes required.',
      to: '/datahub',
    },
    {
      icon: Globe,
      title: 'Value Space',
      tagline: 'Business Impact',
      description:
        'The real-world business outcomes HiveMQ customers ship on the platform: reliability gains, OEE improvements, AI-ready data pipelines, and measurable ROI.',
      to: '/valuespace',
    },
  ]

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        title="Education"
        subtitle="The protocols, patterns, and operational practices behind HiveMQ. Start with the protocol fundamentals, then layer in the architectural ideas that make real industrial deployments work."
      />

      <VStack align="stretch" gap={10}>
        <HStack gap={2} flexWrap="wrap" justify="center">
          {tags.map((tag) => (
            <Box key={tag} px={3} py={1} bg="brand.solid" borderRadius="full">
              <Text fontSize="sm" fontWeight="medium" color="gray.900">
                {tag}
              </Text>
            </Box>
          ))}
        </HStack>

        <VStack align="stretch" gap={4}>
          <SectionHeading
            title="Explore the topics"
            description="Seven topics from protocol fundamentals to security and business outcomes."
          />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
            {topics.map((topic) => (
              <TopicCard key={topic.title} {...topic} />
            ))}
          </SimpleGrid>
        </VStack>
      </VStack>
    </Box>
  )
}
